import { describeBound, momentOfStored, resolveBounds, satisfiesBound } from './dateBounds';
import { allowsMultipleDates, describeWeekdays, isWeekdayAllowed, toDateList, weekdaysOf } from './dateOptions';
import { formatNumber } from './elementFormat/number';
import { isValidEmail } from './elementFormat/email';
import { LAT_LIMIT, LNG_LIMIT, locationKind, parseLocation } from './elementFormat/location';
import { DEFAULT_COUNTRY, normalizeCountry, parsePhone } from './elementFormat/phone';
import { countryName } from './countries';
import { normalizeCurrency } from './currency';
import { allowedSchemesText, normalizeUrl } from './elementFormat/url';
import { normalizeColor } from './elementFormat/color';
import { ceilingFor, countChars, formatLimit, isOverLimit, lengthLimitFor } from './elementFormat/text';
import { matchesPattern } from './elementFormat/pattern';
import { patternMessageFor, storedTextValue } from './textRules';
import { ratingScale } from './rating';

/**
 * Per-data-type value validators — the web half of the write rules the API
 * enforces (docs/plans/data-types.md, "Wire contract"). Each returns a
 * sentence to show under the field, or null when the value is acceptable.
 *
 * Two rules hold the mechanism together:
 *  - an EMPTY value is never invalid here. "You must fill this in" is the
 *    `required` option's job (getElementValidationErrors below runs that
 *    check first), so a blank optional field is simply blank.
 *  - the messages mirror the API's error codes (`range`, `type`), so a 400
 *    that names an element can be shown against the same field with the same
 *    words rather than as a toast the reader has to translate.
 *
 * The registry is keyed by dataType and consulted by name, which is how
 * `phone`, `email` and `location` joined it without touching any of the
 * callers. Those three check their values with the SHARED modules the API
 * validates with (utils/elementFormat/*), vendored byte-identical, so a
 * number the form accepts is a number the save accepts.
 */

// The bounds the card editor offers and the API enforces.
export const NUMBER_LIMITS = {
	MAX_DECIMALS: 6,
	MIN_LEADING_ZEROS: 2,
	MAX_LEADING_ZEROS: 20,
	// The display prefix ("WO-", "INV-"), and the ceiling on a sequence's
	// "increment by". Both mirror the API gate (utils/typedElements.js,
	// utils/sequenceUtils.js) so the editor says it before the round trip.
	MAX_PREFIX: 16,
	MAX_SEQUENCE_STEP: 1000000,
};

const labelOf = element => element?.label?.singular || element?.name || 'This field';

// Blankness as a VALIDATOR sees it. Deliberately not utils/elements
// isElementValueEmpty: elements.js is the module that calls this one
// (getElementValidationErrors), and importing it back would make a cycle out
// of two files that each want to load first. The types here hold scalars, so
// the three blank spellings are the whole of it.
const isBlank = value => value === undefined || value === null || value === '';

const finite = v => (typeof v === 'number' && Number.isFinite(v) ? v : null);

// A bound reads back in the element's own display form, so "at least 010"
// matches what the field shows once it is filled in.
const boundText = (bound, options) => formatNumber(bound, options) || String(bound);

function validateNumber(value, element) {
	const options = element?.options || {};
	const num = typeof value === 'number' ? value : Number(value);
	if (!Number.isFinite(num)) return `${labelOf(element)} must be a number.`;

	const min = finite(options.min);
	const max = finite(options.max);

	// 'Disallow negatives' is a floor of zero unless a larger one is set — the
	// same precedence the input's `min` uses, so the stepper and the message
	// can never disagree.
	if (options.nonNegative && num < 0 && (min === null || min < 0)) {
		return `${labelOf(element)} cannot be negative.`;
	}
	if (min !== null && num < min) return `${labelOf(element)} must be at least ${boundText(min, options)}.`;
	if (max !== null && num > max) return `${labelOf(element)} must be at most ${boundText(max, options)}.`;
	return null;
}

function validatePhone(value, element) {
	if (typeof value !== 'string') return `${labelOf(element)} must be a phone number.`;
	const country = element?.options?.country;
	if (parsePhone(value, country).valid) return null;
	// Names the region, because that is the part the reader cannot see: the
	// same digits are a real number in one country and not in another, and
	// "not valid" without the country reads as an accusation about the digits.
	return `Enter a valid phone number for ${countryName(normalizeCountry(country) || DEFAULT_COUNTRY)}.`;
}

function validateEmail(value, element) {
	if (typeof value !== 'string') return `${labelOf(element)} must be an email address.`;
	return isValidEmail(value) ? null : 'Enter a valid email address.';
}

// The shared module's refusal tokens, in the words the field shows. Mirrors
// the API's LOCATION_REASONS (utils/typedElements.js) so a 400 the web did
// not catch first says the same thing this message does.
const LOCATION_REASONS = {
	type: 'Enter an address, or coordinates as "latitude, longitude".',
	coords_pair: 'Latitude and longitude come together — enter both, or neither.',
	lat_range: `Latitude must be between -${LAT_LIMIT} and ${LAT_LIMIT}.`,
	lng_range: `Longitude must be between -${LNG_LIMIT} and ${LNG_LIMIT}.`,
};

function validateLocation(value, element) {
	const parsed = parseLocation(value);
	if (!parsed.valid) return LOCATION_REASONS[parsed.reason] || LOCATION_REASONS.type;
	if (!parsed.value) return null;

	// Then the element's own `accept` has its say: an element that asked for
	// an address will not hold coordinates, and one that asked for
	// coordinates will not hold a street name. 'both' asks nothing.
	const accept = element?.options?.accept;
	const kind = locationKind(parsed.value);
	if (accept === 'address' && kind !== 'address') {
		return `${labelOf(element)} takes an address, not coordinates.`;
	}
	if (accept === 'coordinates' && kind !== 'coordinates') {
		return `${labelOf(element)} takes coordinates, not an address.`;
	}
	return null;
}

// The words each date type's own values are called, for the one message that
// is about the VALUE rather than a bound.
const DATE_TYPE_WORDS = { date: 'a date', 'date-time': 'a date and time', time: 'a time' };

/**
 * A date, date-time or time against its Earliest / Latest bounds — the
 * element's own (`options.min` / `options.max`) and the implicit floor a range
 * end carries (never before its start). `context` is what the bounds are
 * resolved against: `{ elements, values, now }`, the form's whole element list
 * and its current values, because a bound may name ANOTHER element and must
 * therefore be re-read every time either side changes.
 *
 * Mirrors the API's 400 `range` `{ element, bound, against?, limit? }` —
 * which is why a refusal that reaches the form from the server can be printed
 * against the same field in the same words (utils/apiErrors).
 */
function validateOneDate(value, element, context) {
	const mode = element?.dataType;
	const moment = momentOfStored(mode, value, context?.now);
	if (!moment) {
		return `${labelOf(element)} must be ${DATE_TYPE_WORDS[mode] || 'a date'}.`;
	}
	const { min, max } = resolveBounds(element, context || {});
	if (!satisfiesBound(mode, value, min, 'min', context)) {
		return `${labelOf(element)} must be ${describeBound(min, 'min')}.`;
	}
	if (!satisfiesBound(mode, value, max, 'max', context)) {
		return `${labelOf(element)} must be ${describeBound(max, 'max')}.`;
	}
	// The WEEKDAY rule is not a floor or a ceiling — it is about which days of
	// the week a value may land on at all, so it is checked after the span and
	// said in its own words ("must fall on a weekday"). `date` elements only.
	const weekdays = weekdaysOf(element);
	if (weekdays && !isWeekdayAllowed(moment, weekdays)) {
		return `${labelOf(element)} must fall on ${describeWeekdays(weekdays)}.`;
	}
	return null;
}

/**
 * A date value, which may be a LIST (utils/dateOptions `allowMultiple`).
 * Every entry is judged by the same rules — the bounds apply to each, not to
 * the span they cover — and the FIRST complaint is the field's message, since
 * one field shows one sentence.
 *
 * Both mismatched shapes are still read: an array under an element whose
 * multi is off is judged on its first value, and a scalar under a multi
 * element is a one-entry list. Turning the option on or off must never make
 * a stored row unsaveable.
 */
function validateDate(value, element, context) {
	if (!Array.isArray(value)) return validateOneDate(value, element, context);
	const list = toDateList(value);
	if (!list.length) return null;
	if (!allowsMultipleDates(element)) return validateOneDate(list[0], element, context);
	for (const entry of list) {
		const message = validateOneDate(entry, element, context);
		if (message) return message;
	}
	return null;
}

/**
 * Money, through the SHARED normaliser the API writes with
 * (utils/currency, vendored byte-identical) — so the form refuses exactly
 * what the save would: an over-precise amount (`type`), a currency the
 * element does not allow (`invalid_currency`), a negative under "No
 * negative amounts" or an amount outside Minimum / Maximum (`range`).
 */
function validateCurrency(value, element) {
	try {
		normalizeCurrency(value, element?.options || {});
		return null;
	} catch (err) {
		const label = labelOf(element);
		const text = String(err?.message || '');
		if (err?.code === 'range') {
			if (/negative/.test(text)) return `${label} cannot be negative.`;
			if (/^must be/.test(text)) return `${label} ${text}.`;
			return `${label} is too large an amount.`;
		}
		if (err?.code === 'invalid_currency') return `${text.charAt(0).toUpperCase()}${text.slice(1)}.`;
		const precise = text.match(/more decimal places than (\w+) allows \((\d+)\)/);
		if (precise) {
			return precise[2] === '0'
				? `${precise[1]} amounts have no decimal places.`
				: `${precise[1]} amounts have at most ${precise[2]} decimal places.`;
		}
		return `${label} must be an amount.`;
	}
}

// A user value is an account id — 24 hex characters, the platform's id
// shape — or, with `allowMultiple`, a list of them. The picker only ever
// emits ids, so this catches a pasted or imported value the API would refuse
// (400 `type`). Whether the account EXISTS is the API's question
// (`user_not_found`), answered on save.
const HEX_ID = /^[0-9a-fA-F]{24}$/;

function validateUser(value, element) {
	const multi = !!element?.options?.allowMultiple;
	if (Array.isArray(value)) {
		const entries = value.filter(entry => entry !== undefined && entry !== null && entry !== '');
		// The API refuses ANY non-empty list on a single element (a cleared
		// list is a cleared field): a value left over from a card that once
		// allowed several has to be picked again, one entry or not.
		if (!multi && entries.length) return 'Pick one person for this field.';
		const bad = entries.find(entry => typeof entry !== 'string' || !HEX_ID.test(entry.trim()));
		return bad === undefined ? null : 'Pick people from the list.';
	}
	if (typeof value !== 'string' || !HEX_ID.test(value.trim())) return 'Pick a person from the list.';
	return null;
}

/**
 * THE ECHO RULE, for the rules the data types second pass added (the URL
 * rule, the text ceilings, the rating scale — docs/plans/data-types-second-
 * pass.md PR 1): a value identical to the one the item already stores always
 * saves on the API, so the form must not refuse it either — or an item
 * holding a link or a paragraph from before the rule could never be saved
 * again from this form. `context.stored` is the item's stored element map
 * (the item form passes it for an existing item; a new one has none).
 */
const isEcho = (element, value, context) => {
	const stored = context?.stored;
	return !!stored && !!element?.name
		&& Object.prototype.hasOwnProperty.call(stored, element.name)
		&& stored[element.name] === value;
};

/**
 * A text value against its element's rules (utils/elementFormat/text.js and
 * pattern.js, vendored from keepr-api), judged as it will be STORED — a short
 * text trimmed and cased as its element says (PR 2):
 *  - its limit: the author's own maxLength, else the ceiling — short text
 *    255 characters, long text 20,000 (50,000 with "Allow longer text"),
 *    rich text 100,000 (250,000). The API's 400 `too_long { element, limit }`.
 *  - a short text's pattern: the whole value must match; the message is the
 *    author's sentence. The API's 400 `pattern { element }`.
 * A value the item already stores is never flagged (the echo rule).
 */
function validateLength(value, element, context) {
	if (typeof value !== 'string' || isEcho(element, value, context)) return null;
	const stored = storedTextValue(value, element);
	if (isOverLimit(stored, element?.dataType, element?.options)) {
		const limit = lengthLimitFor(element?.dataType, element?.options);
		return `${labelOf(element)} is limited to ${formatLimit(limit)} characters — this has ${formatLimit(countChars(stored))}.`;
	}
	if (element?.dataType === 'text-small' && stored !== '' && element?.options?.pattern
		&& !matchesPattern(stored, element.options.pattern)) {
		return patternMessageFor(element) || `${labelOf(element)} does not have the expected format.`;
	}
	return null;
}

// The shared URL rule's refusal tokens, in the words the field shows. Mirrors
// the API's URL_REASONS (utils/typedElements.js) so a 400 the web did not
// catch first says the same thing this message does; the allowed schemes
// come from the one list in the vendored module.
const URL_REASONS = {
	whitespace: "A link can't contain spaces.",
	scheme: `Links start with ${allowedSchemesText()}.`,
	no_host: 'Add the site after "https://", like https://example.com.',
	no_target: 'Add what the link points to after the colon.',
	not_a_url: 'Enter a web address like example.com, or a link such as mailto:name@example.com.',
};

/**
 * A link, through the SHARED rule the API writes with (utils/elementFormat/
 * url.js): a bare host is fine (the field adds "https://" on blur, and the
 * API stores it that way); a scheme off the list, a space, or something that
 * is neither a link nor a host is refused — the API's 400 `invalid_url`.
 */
function validateUrl(value, element, context) {
	if (isEcho(element, value, context)) return null;
	if (typeof value !== 'string') return `${labelOf(element)} must be a link.`;
	const parsed = normalizeUrl(value);
	if (parsed.valid) return null;
	if (parsed.reason === 'too_long') return `${labelOf(element)} is limited to ${formatLimit(ceilingFor('url'))} characters.`;
	return URL_REASONS[parsed.reason] || URL_REASONS.not_a_url;
}

/**
 * A color, through the SHARED rule the API writes with (utils/elementFormat/
 * color.js): a hex with its "#", rgb(…) or one of the 22 choice color names;
 * anything else is the API's 400 `invalid_color`. The picker only ever emits
 * a hex, so this catches a stored or pasted value — and the echo lets a value
 * from before the element was a color save unchanged.
 */
function validateColor(value, element, context) {
	if (isEcho(element, value, context)) return null;
	if (typeof value !== 'string') return `${labelOf(element)} must be a color.`;
	if (normalizeColor(value).valid) return null;
	return 'Enter a color as a hex like #1f6feb, as rgb(31, 111, 235), or as a color name like DodgerBlue.';
}

/**
 * A rating against its scale (utils/rating): 0 to the element's stars, in
 * whole stars or halves. The picker cannot produce anything else, so this
 * catches a stored or imported value — the API's `range` / `type`.
 */
function validateRating(value, element, context) {
	if (isEcho(element, value, context)) return null;
	const num = typeof value === 'number' ? value : Number(value);
	if (!Number.isFinite(num)) return `${labelOf(element)} must be a rating.`;
	const { max, allowHalf } = ratingScale(element?.options);
	if (num < 0 || num > max) return `${labelOf(element)} must be between 0 and ${max}.`;
	if (allowHalf ? !Number.isInteger(num * 2) : !Number.isInteger(num)) {
		return allowHalf ? `${labelOf(element)} takes half stars.` : `${labelOf(element)} takes whole stars.`;
	}
	return null;
}

const VALIDATORS = {
	number: validateNumber,
	'text-small': validateLength,
	'text-large': validateLength,
	'rich-text': validateLength,
	url: validateUrl,
	rating: validateRating,
	user: validateUser,
	decimal: validateNumber,
	integer: validateNumber,
	currency: validateCurrency,
	phone: validatePhone,
	email: validateEmail,
	location: validateLocation,
	date: validateDate,
	'date-time': validateDate,
	time: validateDate,
	color: validateColor,
	// No `confirmations` entry (KPR-390): nobody types a "Me too" count, so
	// there is no value of a person's to judge — the form shows it read-only
	// and never sends it, and getElementValidationErrors skips it as server
	// assigned (utils/elements.js isServerAssignedElement).
};

/**
 * The message for ONE element's value, or null. Empty values pass (see the
 * note above); a type with no validator passes too — the platform renders
 * unknown types as text rather than refusing them.
 *
 * `context` carries what a rule cannot read off the element alone — the date
 * bounds' `{ elements, values, now }`, and `stored` (the item's stored
 * element map) for the echo rule the URL, length and rating checks keep.
 * Every other validator ignores it, and a date with no bounds needs nothing
 * from it.
 */
export function validateElementValue(element, value, context = undefined) {
	if (!element?.dataType) return null;
	if (isBlank(value)) return null;
	const validate = VALIDATORS[element.dataType];
	return validate ? validate(value, element, context) : null;
}

/** Does this data type validate its values at all? (Used by the blur hook.) */
export const hasValueValidator = dataType => !!VALIDATORS[dataType];
